import { useCallback, useEffect, useState } from 'react';
import { LS_KEY_AI_ASSISTANT_NUDGE_SEEN } from 'src/constants/localStorageKeys';
import {
  ASSISTANT_MOMENT_EVENT,
  type AssistantMoment,
} from 'src/helpers/assistantMoments';
import { extensionStatus } from 'src/helpers/extensionBridge';
import useTabVisible from 'src/helpers/hooks/useTabVisible';
import { useAppDispatch } from 'src/redux';
import { fetchAssistantNudge } from 'src/redux/actions/assistant';
import type { AssistantNudge } from 'src/redux/types/assistant';
import { readDay, updateDay, useAssistantDay } from './assistantDay';
import type { OrbGreeting } from './personas';

/** The tab in view this long before the day's greeting: they've settled in. */
const GREET_AFTER_MS = 6000;
/** A moment older than this has passed: brought up now it would read as random. */
const MOMENT_FRESH_MS = 10 * 60 * 1000;
/** Between two moments' bubbles, so a run of applications brings up one. */
const MOMENT_GAP_MS = 2 * 60 * 1000;
const HOP_MS = 1300;
/** How often it looks whether the way is clear to speak. */
const CHECK_EVERY_MS = 1500;

const isModalOpen = () =>
  !!document.querySelector('.MuiModal-root:not(.MuiModal-hidden)');

const firstEver = () => {
  try {
    return localStorage.getItem(LS_KEY_AI_ASSISTANT_NUDGE_SEEN) !== '1';
  } catch {
    return false;
  }
};

const markSeen = () => {
  try {
    localStorage.setItem(LS_KEY_AI_ASSISTANT_NUDGE_SEEN, '1');
  } catch {
    // Private mode: they get the hello copy again, which is harmless.
  }
};

/** What the orb shows right now: the bubble's words, and the hop. */
export interface OrbPrompt {
  /** What it said, until they dismiss it or open the panel. */
  bubble: AssistantNudge | null;
  hopping: boolean;
  dismiss: () => void;
}

/**
 * When the orb speaks up. Once a day, if the chat hasn't been opened, it hops
 * and greets them (the persona's greeting). After that it speaks only on
 * something the user just did (assistantMoments): right after they apply to a
 * job, open a company's application, download a resume or finish a lesson,
 * it brings up the next thing worth doing while they're here -- most visit
 * once, so a timer would speak to an empty room. Only while someone is
 * looking at the tab and nothing else is in the way: a moment waits out a
 * dialog or the panel. What it says stays beside it, on every page and
 * after a reload, until they dismiss it or open the panel. The day's record
 * is shared by every tab, so two never both speak.
 */
const useOrbPrompt = ({
  userId,
  name,
  greeting,
  enabled,
  blocked,
}: {
  userId: string;
  name: string;
  greeting: OrbGreeting | null;
  /** Signed in with an assistant: what they do is noted for the orb. */
  enabled: boolean;
  /** The panel is open, or the page is one not to interrupt. */
  blocked: boolean;
}): OrbPrompt => {
  const dispatch = useAppDispatch();
  const visible = useTabVisible();
  const day = useAssistantDay(userId);
  const [hopping, setHopping] = useState(false);

  const hop = useCallback(() => {
    setHopping(true);
    window.setTimeout(() => setHopping(false), HOP_MS);
  }, []);

  // Noted even where the orb is hidden (a lesson's own page): it speaks once
  // they are somewhere it shows.
  useEffect(() => {
    if (!userId || !enabled) return undefined;
    const onMoment = (e: Event) => {
      const { kind, subject } = (e as CustomEvent<AssistantMoment>).detail;
      updateDay(userId, () => ({ moment: { kind, subject, at: Date.now() } }));
    };
    window.addEventListener(ASSISTANT_MOMENT_EVENT, onMoment);
    return () => window.removeEventListener(ASSISTANT_MOMENT_EVENT, onMoment);
  }, [userId, enabled]);

  useEffect(() => {
    if (!userId || !enabled || blocked || !visible || !greeting)
      return undefined;
    const visibleSince = Date.now();
    let asking = false;

    const ask = async (shown: string[], moment?: AssistantMoment) => {
      asking = true;
      const said = await dispatch(
        fetchAssistantNudge({
          skip: shown,
          extension: extensionStatus(),
          moment,
        }),
      )
        .unwrap()
        .catch(() => null);
      asking = false;
      return said;
    };

    const say = (said: AssistantNudge, counted: boolean) => {
      updateDay(userId, ({ shown }) => ({
        shown: counted ? [...shown, said.key] : shown,
        pending: said,
      }));
      hop();
    };

    const check = async () => {
      if (asking || isModalOpen()) return;
      const now = Date.now();
      const today = readDay(userId);
      const { moment } = today;
      if (moment && now - moment.at > MOMENT_FRESH_MS) {
        updateDay(userId, () => ({ moment: null }));
        return;
      }
      if (moment) {
        if (now - (today.nudgeAt ?? 0) < MOMENT_GAP_MS) return;
        // Claimed first: another tab looking now finds it gone.
        updateDay(userId, () => ({ moment: null, nudgeAt: now }));
        const said = await ask(today.shown, moment);
        if (said) say(said, true);
        return;
      }
      if (today.openedAt || today.promptedAt) return;
      if (now - visibleSince < GREET_AFTER_MS) return;
      updateDay(userId, () => ({ promptedAt: now }));
      // Something timely from their account, when the persona has one to say.
      const found = greeting.fromServer ? await ask(today.shown) : null;
      const hello = greeting.say(name, firstEver());
      markSeen();
      say(found ?? hello, !!found);
    };

    const id = window.setInterval(check, CHECK_EVERY_MS);
    return () => window.clearInterval(id);
  }, [userId, name, greeting, enabled, blocked, visible, dispatch, hop]);

  // Gone from every tab: the panel opening clears it the same way.
  const dismiss = useCallback(
    () => updateDay(userId, () => ({ pending: null })),
    [userId],
  );
  return { bubble: day.pending ?? null, hopping, dismiss };
};

export default useOrbPrompt;
